/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { ConsumptionType, ContentAuthorizationData } from 'fl-content-authorization';
import { AdBreak, BUFFERING_STATES, PLAYBACK_STATES, Player, SEEKING_STATES } from 'fl-player-interface';
import { NewrelicEvent, QPNewrelicService } from 'qp-newrelic';
import { ErrorSeverityType } from 'qp-store';
import { ProgramVm, ResourceVm } from 'qp-ui-interface';
import { LocalStorageKeys } from 'src/app/shared/LocalStorageKeys';
import { ConfigService } from 'src/app/shared/services/config/config.service';
import { LocalStorageService } from 'src/app/shared/services/local-storage/local-storage.service';
import { ScreenService } from 'src/app/shared/services/screen/screen.service';
import { getAdInfo } from 'src/app/shared/utils/EventUtlis';
import { getPlayerErrorInfo } from 'src/app/shared/utils/playerUtils';
import { NewrelicError } from '../../enums/errors';
import { StoreService } from '../../store/store.service';
import { getHasAdInfo, getNRPlaybackStatistics } from '../../utils/playbackEventUtils';
import { NewrelicService } from '../newrelic.service';

declare const newrelic: any;

@Injectable({
  providedIn: 'root',
})
export class PlaybackNewrelicService extends QPNewrelicService {
  private playerInstance: Player;
  private hasAds: boolean;
  private progressInterval;
  private started = false;
  private playbackStartedTime = 0;
  private currentStartedInitialTime: number;
  private resource: ResourceVm;
  private adStarted = false;
  private playbackMode: string;

  constructor(
    storeService: StoreService,
    private configService: ConfigService,
    private localStorage: LocalStorageService,
    private newrelicService: NewrelicService,
    private screenService: ScreenService
  ) {
    super(storeService, newrelic, false);
  }

  /**
   * Attatch player and subscribe to all the events to start tracking with Newrelic
   *
   * @param player
   */
  attachPlayer(player: Player, hasAds: boolean, resource: ResourceVm, playbackMode: string): void {
    this.playerInstance = player;
    this.hasAds = hasAds;
    this.resource = resource;
    this.playbackMode = playbackMode;
    const gracenotes = this.store.getGracenoteInfo();
    if (['catchup', ConsumptionType.LIVE].includes(playbackMode) && gracenotes.gracenoteGenres) {
      // only for programs
      this.resource.contentGenre = gracenotes.gracenoteGenres.split(',');
    }
    const finalResource = this.resource.contentType === 'channel' ? { ...this.resource, contentType: 'airing' } : this.resource;
    this.store.setResource(finalResource);
    this.subscribeToplayerInstanceEvents();
    if (hasAds) {
      this.subscribeAdPlayerEvents();
    }
    this.initStartedTime();
  }

  setPlayerInfo(
    player: Player,
    authorizationData: ContentAuthorizationData,
    playbackMode: string,
    sid: string,
    streamId: string,
    programSid?: string
  ): void {
    this.store.setPlayerInfo({
      drmName: authorizationData.drmScheme,
      drmVersion: player.internalPlayerVersion,
      playbackURL: authorizationData.contentUrl,
      playerName: player.internalPlayerName ?? 'shaka-player',
      playbackMode,
      playbackType: authorizationData?.mediaType,
      streamID: streamId,
      playbackSessionID: sid,
      programPlaybackSessionID: programSid,
    });
  }

  /**
   * Detach player instance
   * Detach resource
   */
  detachPlayer(): void {
    this.store.setResource(undefined);
    this.store.setPlayerInfo(undefined);
    this.store.setChannel(undefined);
    this.store.setGracenote(undefined);
    this.unsubscribeAdPlayerEvents();
    this.unsubscribeFromplayerInstanceEvents();
    this.hasAds = undefined;
  }

  sendError(errorType: NewrelicError, errorCode: number | string, errorMessage: string, errorDescription: string, error: any): void {
    this.newrelicService.sendErrorEvent(
      errorType,
      errorCode,
      errorMessage,
      errorDescription,
      error,
      ErrorSeverityType.CRITICAL,
      undefined,
      error?.internalError?.errorCode?.toString()
    );
  }

  sendSkip(intro?: boolean): void {
    if (intro) {
      this.newrelicService.sendEvent(NewrelicEvent.SKIP_INTRO);
    } else {
      this.newrelicService.sendEvent(NewrelicEvent.SKIP_CREDITS);
    }
  }

  sendNextEpisode(): void {
    this.newrelicService.sendEvent(NewrelicEvent.NEXT_EPISODE);
  }

  // sendCatchUp(): void {
  //   this.newrelicService.sendEvent(NewrelicEvent.CATCH_UP);
  // }

  setProgramAttributes(id: string, program: ProgramVm): void {
    const isSports = program.type.toLowerCase().includes('sport');
    const vsNames = isSports && program.name?.includes('vs') ? program.name.split(/\s*vs.\s*/).map((word) => word.trim()) : [];
    this.store.setExternalInfo({
      enrichmentContentID: id,
      externalContentID: 'NA',
    });
    console.log('Program attributes', program);
    this.store.setGracenote({
      gracenoteTMSID: id,
      gracenoteRootID: program.rootId,
      gracenoteSeriesID: program.seriesId,
      gracenoteSubType: program.subtype,
      gracenoteTitle: program.name,
      gracenoteEntityType: program.type,
      gracenoteGenres: (program.contentGenre ?? []).join(','),
      gracenoteSportsEventTitle: isSports ? program.name : '',
      gracenoteSportsTeamBrandId: isSports ? program?.sportTeamId ?? '' : '',
      gracenoteSportsTeamNameHome: isSports ? (vsNames.length === 2 ? vsNames[0] : program.name) : '',
      gracenoteSportsTeamNameAway: isSports ? (vsNames.length === 2 ? vsNames[1] : program.name) : '',
    });
  }
  /**
   * Checkout store still has valid resource attached
   *
   * @returns boolean
   */
  isValidResource(): boolean {
    const { contentID, contentName, contentType } = this.store.getContentInfo();

    if ((!contentID || contentID === 'NA') && (!contentName || contentName === 'NA') && (!contentType || contentType === 'NA')) {
      return false;
    }

    return true;
  }

  /**
   * Subscribe to player events.
   *
   * @param player
   */
  private subscribeToplayerInstanceEvents = (): void => {
    if (!this.playerInstance) {
      return;
    }
    this.playerInstance.subscribe('contentloaded', this.onContentLoaded);
    this.playerInstance.subscribe('bufferingstatechanged', this.onBufferingStateChanged);
    this.playerInstance.subscribe('streamended', this.onStreamEnded);
    this.playerInstance.subscribe('seekingstatechanged', this.onSeekingStateChanged);
    this.playerInstance.subscribe('playbackstatechanged', this.onPlaybackStateChanged);
    this.playerInstance.subscribe('bandwidthchanged', this.onBandwidthChanged);
    this.playerInstance.subscribe('error', async () => this.onError());
  };

  /**
   * Unsubscribe from player events.
   *
   * @param player
   */
  private unsubscribeFromplayerInstanceEvents = (): void => {
    if (!this.playerInstance) {
      return;
    }
    this.playerInstance.unsubscribe('bufferingstatechanged', this.onBufferingStateChanged);
    this.playerInstance.unsubscribe('streamended', this.onStreamEnded);
    this.playerInstance.unsubscribe('seekingstatechanged', this.onSeekingStateChanged);
    this.playerInstance.unsubscribe('playbackstatechanged', this.onPlaybackStateChanged);
    this.playerInstance.unsubscribe('bandwidthchanged', this.onBandwidthChanged);
    this.playerInstance.unsubscribe('error', this.onError);
  };

  private subscribeAdPlayerEvents(): void {
    if (!this.hasAds) {
      return;
    }
    this.playerInstance.subscribe('adstart', (event) => this.onAdStart(event));
    this.playerInstance.subscribe('adbreakstart', (event) => this.onAdBreakStart(event));
    this.playerInstance.subscribe('adbreakend', () => this.onAdBreakEnd());
    this.playerInstance.subscribe('adend', (event) => this.onAdEnd(event));
    this.playerInstance.subscribe('aderror', (event) => this.onAdError(event));
    this.playerInstance.subscribe('adverttrackingevent', (event) => this.onAdTracking(event));
  }

  private unsubscribeAdPlayerEvents(): void {
    if (!this.hasAds) {
      return;
    }
    this.playerInstance.unsubscribe('adstart', (event) => this.onAdStart(event));
    this.playerInstance.unsubscribe('adbreakstart', (event) => this.onAdBreakStart(event));
    this.playerInstance.unsubscribe('adbreakend', () => this.onAdBreakEnd());
    this.playerInstance.unsubscribe('adend', (event) => this.onAdEnd(event));
    this.playerInstance.unsubscribe('aderror', (event) => this.onAdError(event));
    this.playerInstance.unsubscribe('adverttrackingevent', (event) => this.onAdTracking(event));
  }

  /**
   * Manage on buffering state change player event
   *
   * @param bufferingState
   */
  private onBufferingStateChanged = (bufferingState: BUFFERING_STATES): void => {
    switch (bufferingState) {
      case BUFFERING_STATES.ACTIVE: {
        this.updatePlaybackStats();
        this.newrelicService.sendEvent(NewrelicEvent.PLAYBACK_BUFFERING_START);
        break;
      }
      case BUFFERING_STATES.INACTIVE: {
        this.updatePlaybackStats();
        this.newrelicService.sendEvent(NewrelicEvent.PLAYBACK_BUFFERING_END);
        break;
      }
    }
  };

  /**
   * Manage on stream end player event
   */
  private onStreamEnded = (): void => {
    this.removeContentStartTime();
    this.updatePlaybackStats();
    this.newrelicService.sendEvent(NewrelicEvent.PLAYBACK_COMPLETE);
    this.stopProgressInterval();
  };

  /**
   * Manage on content loaded player event
   */
  private onContentLoaded = (): void => {
    this.started = false;
    this.getPlaybackStartedTime(this.currentStartedInitialTime);
    this.updatePlaybackStats();
    this.newrelicService.sendEvent(NewrelicEvent.PLAYBACK_PREPARED);
  };

  /**
   * Manage seeking state change player event
   *
   * @param seekingState
   */
  private onSeekingStateChanged = (seekingState: SEEKING_STATES): void => {
    switch (seekingState) {
      case SEEKING_STATES.ACTIVE: {
        this.updatePlaybackStats();
        this.newrelicService.sendEvent(NewrelicEvent.PLAYBACK_SEEK_START);
        break;
      }
      case SEEKING_STATES.INACTIVE: {
        this.updatePlaybackStats();
        this.newrelicService.sendEvent(NewrelicEvent.PLAYBACK_SEEK_COMPLETE);
      }
    }
  };

  /**
   * Manage playback state change player event
   *
   * @param playbackState
   */
  private onPlaybackStateChanged = (playbackState: PLAYBACK_STATES): void => {
    switch (playbackState) {
      case PLAYBACK_STATES.LOADING: {
        break;
      }
      case PLAYBACK_STATES.LOADED: {
        break;
      }
      case PLAYBACK_STATES.STARTED: {
        if ((this.playerInstance?.currentTime === 0 || this.started === false) && this.started !== true) {
          this.updatePlaybackStats();
          this.newrelicService.sendEvent(NewrelicEvent.PLAYBACK_START);
          this.newrelicService.sendEvent(NewrelicEvent.PLAYBACK_STARTED);
          this.playbackStartedTime = null;
          this.started = true;
          this.startProgressInterval();
        } else if (this.hasAds && this.adStarted) {
          this.updatePlaybackStats();
          this.newrelicService.sendEvent(NewrelicEvent.PLAYBACK_START);
          this.newrelicService.sendEvent(NewrelicEvent.PLAYBACK_STARTED);
          this.playbackStartedTime = null;
          this.adStarted = false;
          this.startProgressInterval();
        } else {
          this.updatePlaybackStats();
          this.newrelicService.sendEvent(NewrelicEvent.PLAYBACK_RESUMED);
          this.startProgressInterval();
        }
        break;
      }
      case PLAYBACK_STATES.PAUSED: {
        this.updatePlaybackStats();
        this.newrelicService.sendEvent(NewrelicEvent.PLAYBACK_PAUSED);
        this.stopProgressInterval();
        break;
      }
      case PLAYBACK_STATES.STOPPED: {
        this.removeContentStartTime();
        this.updatePlaybackStats();
        this.newrelicService.sendEvent(NewrelicEvent.PLAYBACK_STOPPED);
        this.stopProgressInterval();
        break;
      }
      case PLAYBACK_STATES.IDLE: {
        this.removeContentStartTime();
        this.updatePlaybackStats();
        this.newrelicService.sendEvent(NewrelicEvent.PLAYBACK_STOPPED);
        this.stopProgressInterval();
        break;
      }
    }
  };

  /**
   *  remove contentStartTime
   */

  private removeContentStartTime() {
    this.localStorage.removeItem(LocalStorageKeys.CONTENT_START_TIME);
  }

  /**
   * Start progress interval report
   */
  private startProgressInterval = (): void => {
    try {
      const interval = this.configService.applicationConfig.playbackReportingIntervalMs
        ? parseInt(this.configService.applicationConfig.playbackReportingIntervalMs, 10)
        : 60000;
      this.stopProgressInterval();
      this.progressInterval = setInterval(() => {
        this.updatePlaybackStats();
        const isValidResource = this.isValidResource();
        if (!isValidResource) {
          this.stopProgressInterval();
        } else {
          this.newrelicService.sendEvent(NewrelicEvent.PLAYBACK_INPROGRESS);
        }
      }, interval);
    } catch {}
  };

  /**
   * End progress interval report
   */
  private stopProgressInterval = (): void => {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = undefined;
    }
  };

  /**
   * Manage bandwith change player event
   */
  private onBandwidthChanged = (): void => {
    if (this.playerInstance?.playbackStatistics) {
      this.updatePlaybackStats();
      // this.newrelicService.sendEvent(NewrelicEvent.PLAYBACK_VARIANT_CHANGE);
    }
  };

  private onAdBreakStart(event: AdBreak): void {
    this.updateAdInfo(event);
    this.newrelicService.sendEvent(NewrelicEvent.AD_BREAK_START);
  }

  private onAdStart = (event): void => {
    this.adStarted = true;
    this.updateAdInfo(event);
    this.newrelicService.sendEvent(NewrelicEvent.AD_START); //Pending ad URL (not being sent in tgz v.52)
  };

  private onAdTracking(adTrackingEvent): void {
    switch (adTrackingEvent) {
      case 'Ad Skipped': // Ad was skipped
        this.newrelicService.sendEvent(NewrelicEvent.AD_SKIP);
        break;
      case 'Ad Click': // Ad LEARN MORE link was clicked
        this.newrelicService.sendEvent(NewrelicEvent.AD_VIEW);
        break;
    }
  }

  private onAdEnd = (event): void => {
    this.updateAdInfo(event);
    this.newrelicService.sendEvent(NewrelicEvent.AD_END);
  };

  private onAdBreakEnd(): void {
    this.newrelicService.sendEvent(NewrelicEvent.AD_BREAK_COMPLETE);
  }

  private onAdError(error): void {
    if (this.hasAds) {
      const { errorCode, errorMessage } = getPlayerErrorInfo(error);
      const errorAttributes = this.getEventAttributes(NewrelicEvent.ERROR);
      this.newrelicService.sendErrorEvent(
        NewrelicError.AD_ERROR,
        errorCode,
        errorMessage,
        errorMessage,
        {
          ...errorAttributes,
          errorCode,
          errorMessage,
          internalError: '',
          errorInternal: error,
        },
        ErrorSeverityType.CRITICAL
      );
    }
  }

  private updateAdInfo = (event: any): void => {
    this.store.setAdInfo(getAdInfo(event, ''));
    this.store.setScreenInfo({
      currentScreen: this.screenService.currentScreen,
      previousScreen: this.screenService.previousScreen,
    });
  };

  private onError = (): void => {
    setTimeout(() => {
      this.removeContentStartTime();
      this.stopProgressInterval();
      this.updatePlaybackStats();
    }, 0);
  };

  private updatePlaybackStats(): void {
    const playbackStats = getNRPlaybackStatistics(
      this.playerInstance,
      this.resource,
      this.playbackStartedTime,
      this.screenService,
      this.localStorage
    );
    if (this.playbackMode === 'live') {
      playbackStats.contentDurationSec = undefined;
    }
    this.store.setPlaybackStats(playbackStats);
    // Catchup does not show cuepoints
    if (this.hasAds && !['catchup', ConsumptionType.LIVE].includes(this.playbackMode)) {
      this.store.setHasAdInfo(getHasAdInfo(this.playerInstance));
    }
  }

  private initStartedTime(): void {
    this.currentStartedInitialTime = Date.now();
  }

  private getPlaybackStartedTime(initialTime: number): void {
    const endTime = new Date().getTime();
    try {
      this.playbackStartedTime = !initialTime ? 0 : (endTime - initialTime) / 1000;
      this.playbackStartedTime =
        this.playbackStartedTime <= 0
          ? 0
          : this.playbackStartedTime > 50
          ? +(Math.random() * 40 + 10).toFixed(3)
          : this.playbackStartedTime;
    } catch (error) {
      this.playbackStartedTime = 0;
    }
  }
}
